import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { uploadService, UploadResult } from '../../services/uploadService';

const ImageUploader: React.FC = () => {
    const { user } = useAuth();
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
    const [dragActive, setDragActive] = useState(false);
    const [s3Connected, setS3Connected] = useState<boolean | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Test S3 connection on component mount
    useEffect(() => {
        testS3Connection();
    }, []);

    const testS3Connection = async () => {
        try {
            const result = await uploadService.testS3Connection();
            setS3Connected(result.success);
            if (!result.success) {
                console.error('S3 connection failed:', result.message);
            }
        } catch (error) {
            setS3Connected(false);
            console.error('S3 connection test failed:', error);
        }
    };

    // Check admin access
    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            setSelectedFiles(files);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFiles(e.target.files);
        }
    };

    const validateImageFile = (file: File): { valid: boolean; message: string; productCode?: string } => {
        // Check file type
        if (!file.type.startsWith('image/')) {
            return { valid: false, message: 'Le fichier doit être une image' };
        }

        // Check file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            return { valid: false, message: 'Le fichier ne doit pas dépasser 5MB' };
        }

        // Extract product code from filename (should be like "ABC123.jpg")
        const filename = file.name;
        const productCode = filename.replace(/\.[^/.]+$/, ""); // Remove extension

        if (!productCode || productCode.length === 0) {
            return { valid: false, message: 'Le nom du fichier doit correspondre au code produit (ex: ABC123.jpg)' };
        }

        return { valid: true, message: 'Fichier valide', productCode: productCode.toLowerCase() };
    };

    const handleUpload = async () => {
        if (!selectedFiles) return;

        setUploading(true);
        setUploadResults([]);
        setUploadProgress(0);

        try {
            const response = await uploadService.uploadImages(selectedFiles);
            setUploadResults(response.results);
        } catch (error: any) {
            // Handle upload error
            const errorResult: UploadResult = {
                filename: 'Erreur générale',
                success: false,
                message: error.message || 'Erreur lors de l\'upload'
            };
            setUploadResults([errorResult]);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setSelectedFiles(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const getFilePreview = (file: File) => {
        return URL.createObjectURL(file);
    };

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <nav className="text-sm text-eldai-gray mb-4">
                        <Link to="/admin" className="hover:text-eldai-blue">Admin</Link>
                        <span className="mx-2">›</span>
                        <span>Gestionnaire d'images</span>
                    </nav>
                    <h1 className="text-3xl font-bold text-eldai-blue mb-2">📸 Gestionnaire d'images produits</h1>
                    <p className="text-eldai-gray">
                        Uploadez des images de produits vers le stockage S3. Les images doivent être nommées avec le code produit (ex: ABC123.jpg).
                    </p>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-blue-800">📋 Instructions d'utilisation</h2>
                        {/* S3 Connection Status */}
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Statut S3:</span>
                            {s3Connected === null ? (
                                <span className="text-yellow-600">⏳ Test en cours...</span>
                            ) : s3Connected ? (
                                <span className="text-green-600">✅ Connecté</span>
                            ) : (
                                <span className="text-red-600">❌ Erreur connexion</span>
                            )}
                        </div>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-blue-700">
                        <li><strong>Nommage des fichiers</strong> : Le nom de chaque fichier doit correspondre exactement au code produit (ex: <code className="bg-white px-1 rounded">6567.jpg</code>)</li>
                        <li><strong>Conversion automatique</strong> : Les noms de fichiers seront automatiquement convertis en minuscules (ex: ABC123.jpg → abc123.jpg)</li>
                        <li><strong>Format accepté</strong> : JPG</li>
                        <li><strong>Taille maximale</strong> : 5MB par fichier</li>
                        <li><strong>Résolution recommandée</strong> : 800x800 pixels minimum pour une qualité optimale</li>
                        <li><strong>URL générée</strong> : <code className="bg-white px-1 rounded">{process.env.REACT_APP_S3_BASE_URL || 'https://eldai-commands-images.s3.eu-north-1.amazonaws.com'}/[CODE].jpg</code></li>
                    </ul>
                </div>

                {/* Upload Area */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-semibold text-eldai-blue mb-6">📤 Zone d'upload</h2>

                    {/* Drag & Drop Area */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                            ? 'border-eldai-blue bg-blue-50'
                            : 'border-gray-300 hover:border-eldai-blue'
                            }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <div className="text-6xl mb-4">📸</div>
                        <p className="text-xl font-semibold text-eldai-gray mb-2">
                            Glissez et déposez vos images ici
                        </p>
                        <p className="text-eldai-gray mb-4">ou</p>
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="bg-eldai-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                        >
                            📁 Sélectionner des fichiers
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles && selectedFiles.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-eldai-blue mb-4">
                                📋 Fichiers sélectionnés ({selectedFiles.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {Array.from(selectedFiles).map((file, index) => {
                                    const validation = validateImageFile(file);
                                    return (
                                        <div key={index} className="border rounded-lg p-4">
                                            <div className="aspect-square mb-3 bg-gray-100 rounded-lg overflow-hidden">
                                                <img
                                                    src={getFilePreview(file)}
                                                    alt={file.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <p className="font-medium text-sm truncate" title={file.name}>
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                            {validation.productCode && (
                                                <p className="text-xs text-green-600 mt-1">
                                                    Code produit: {validation.productCode}
                                                </p>
                                            )}
                                            {!validation.valid && (
                                                <p className="text-xs text-red-600 mt-1">
                                                    ⚠️ {validation.message}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {uploading ? '⏳ Upload en cours...' : '🚀 Commencer l\'upload'}
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedFiles(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                    className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    ❌ Annuler
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Upload Results */}
                {uploadResults.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold text-eldai-blue mb-6">📊 Résultats de l'upload</h2>
                        <div className="space-y-3">
                            {uploadResults.map((result, index) => (
                                <div
                                    key={index}
                                    className={`p-4 rounded-lg border ${result.success
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <span className="text-2xl mr-3">
                                                {result.success ? '✅' : '❌'}
                                            </span>
                                            <div>
                                                <p className="font-medium">{result.filename}</p>
                                                <p className={`text-sm ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                                                    {result.message}
                                                </p>
                                            </div>
                                        </div>
                                        {result.success && result.productCode && (
                                            <div className="text-right">
                                                <p className="text-sm text-gray-600">URL S3:</p>
                                                <p className="text-xs text-blue-600 font-mono break-all">
                                                    {result.s3Url || `${process.env.REACT_APP_S3_BASE_URL || 'https://eldai-commands-images.s3.eu-north-1.amazonaws.com'}/${result.productCode}.jpg`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <div className="flex justify-between text-sm">
                                <span>Total: {uploadResults.length} fichiers</span>
                                <span className="text-green-600">
                                    Succès: {uploadResults.filter(r => r.success).length}
                                </span>
                                <span className="text-red-600">
                                    Échecs: {uploadResults.filter(r => !r.success).length}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageUploader;
